import React from 'react';
import CustomMedia from 'util/custom-media-query';
import FormDialogrenderer from './FormDialogRenderer';
import i18n from 'i18n';
import { css } from 'styled-components';
import {
  applyStyles,
  replaceHyphenAndSpaces,
  convertDoubleWidthNumber,
  convertKana,
  allowExactKeysOnly
} from 'util/helpers';
import Button from 'components/common/Button';
import TextField from 'components/common/TextField';
import SelectField from 'components/common/SelectField';
import Image from 'components/common/Image';
import imgInvoice2PC1x from 'assets/images/PC/@1x/img_Invoice2.png';
import imgInvoice2PC2x from 'assets/images/PC/@2x/img_Invoice2@2x.png';
import imgInvoice2SP2x from 'assets/images/SP/@2x/img_Invoice2@2x.png';
import imgInvoice2SP3x from 'assets/images/SP/@3x/img_Invoice2@3x.png';
import FontStyles from 'constants/font-styles';
import Prefectures from 'constants/prefectures';
import {
  required,
  mustBeNumber,
  kanaPattern,
  postcodePattern,
  composeValidators,
  justLength
} from 'validation/validators';
import axios from 'axios';
import { get } from 'lodash';
import { keysTemplate } from 'components/OrderAggregators/scripts/order';

const handleClickInsertAddress = async form => {
  const { batch, getState, change } = form;
  const postcode = getState().values.shipperPostcode;

  axios
    .get(
      `${
        process.env.REACT_APP_YGETS_API_BASE_URL
      }/api/address?postalCode=${postcode}`,
      { timeout: 3000 }
    )
    .then(response => {
      const data = response.data;

      if (Object.keys(data).length === 0) {
        return;
      }

      batch(() => {
        change('shipperPrefecture', data.prefecture);
        change('shipperCity', data.city);
      });
    });
};

const handleBlurPostcode = (form, value) => {
  const { change } = form;
  const replaced = convertDoubleWidthNumber(value);
  const formatted = replaceHyphenAndSpaces(replaced);

  change('shipperPostcode', formatted);
  handleChangePostCode(form);
};

const handleBlurNumbers = (form, key, value) => {
  const { change } = form;
  const replaced = convertDoubleWidthNumber(value);
  const formatted = replaceHyphenAndSpaces(replaced);

  change(key, formatted);
};

const handleBlurKana = (form, value) => {
  const { change } = form;
  const replaced = convertKana(value);

  change('shipperNameKana', replaced);
};

const handleChangePostCode = form => {
  const fieldState = form.getFieldState('shipperPostcode');

  if (fieldState.valid) {
    handleClickInsertAddress(form);
  }
};

const createSelectShipperOptions = (options, index) => [
  {
    label: i18n.t('orderAggregators:common:order.select'),
    value: ''
  },
  ...options.map(option => ({
    label: `${option.label}${
      option.value === index
        ? i18n.t('orderAggregators:common:order.copyReceiverValueLabel')
        : i18n.t('orderAggregators:common:order.copyShipperValueLabel')
    }`,
    value: option.value
  }))
];

const changeShipperValues = (values, change, role = 'shipper') =>
  keysTemplate.map(key =>
    change(`shipper${key}`, get(values, `${role + key}`))
  );

const copyShipperInfo = (form, orders, selected, index) => {
  if (selected === '') return;

  const { change } = form;
  if (+selected === index) {
    const receiver = orders[index].receiver;
    return changeShipperValues(receiver, change, 'receiver');
  } else {
    const shipper = orders[+selected].shipper;
    return changeShipperValues(shipper, change);
  }
};

const DialogBody = ({
  fetchAddress,
  form,
  referringDataTemplate,
  isConfirm,
  orders,
  index,
  options
}) => (
  <div className="form">
    <div className="image-wrapper">
      <Image
        pc1x={imgInvoice2PC1x}
        pc2x={imgInvoice2PC2x}
        sp2x={imgInvoice2SP2x}
        sp3x={imgInvoice2SP3x}
        alt="img_invoice"
      />
    </div>

    <div className="fields-container">
      {isConfirm ? (
        <div className="order-content">{referringDataTemplate()}</div>
      ) : (
        <React.Fragment>
          <div className="copy-shipper-wrapper">
            <SelectField
              name="copyShipperValue"
              options={createSelectShipperOptions(options, index)}
              onChange={e =>
                copyShipperInfo(form, orders, e.target.value, index)
              }
            />
            <span>
              {i18n.t('orderAggregators:common:order.copyShipperLabel')}
            </span>
          </div>
          <React.Fragment>
            <TextField
              name="shipperName"
              validate={value => composeValidators(required)(value)}
              label={i18n.t('orderAggregators:common:order.name')}
              maxLength={30}
              placeholder={i18n.t(
                'orderAggregators:common:order.namePlaceholder'
              )}
              required={true}
              notes={[i18n.t('orderAggregators:common:order.nameNotes')]}
            />
            <TextField
              name="shipperNameKana"
              validate={value => kanaPattern(value)}
              label={i18n.t('orderAggregators:common:order.kana')}
              maxLength={30}
              placeholder={i18n.t(
                'orderAggregators:common:order.kanaPlaceholder'
              )}
              notes={[i18n.t('orderAggregators:common:order.kanaNotes')]}
              onBlur={value => handleBlurKana(form, value)}
            />
            <div className="postcode-wrapper">
              <TextField
                type="tel"
                name="shipperPostcode"
                className="input-postcode"
                validate={value =>
                  composeValidators(required, postcodePattern, justLength(7))(
                    value
                  )
                }
                label={i18n.t('orderAggregators:common:order.postcode')}
                maxLength={7}
                placeholder={i18n.t(
                  'orderAggregators:common:order.postcodePlaceholder'
                )}
                required={true}
                onBlur={value => handleBlurPostcode(form, value)}
                onChange={() => handleChangePostCode(form)}
                onKeyDown={event => allowExactKeysOnly(event, /[^-]/)}
              />
              <Button
                buttonType="F"
                className="fill-address"
                type="button"
                onClick={() => handleClickInsertAddress(form, fetchAddress)}
              >
                {i18n.t('orderAggregators:common:order.postcodeInput')}
              </Button>
            </div>
            <SelectField
              name="shipperPrefecture"
              validate={value => required(value)}
              label={i18n.t('orderAggregators:common:order.prefecture')}
              required={true}
              options={Prefectures}
            />
            <TextField
              name="shipperCity"
              validate={value => composeValidators(required)(value)}
              label={i18n.t('orderAggregators:common:order.city')}
              maxLength={20}
              required={true}
              placeholder={i18n.t(
                'orderAggregators:common:order.cityPlaceholder'
              )}
              notes={[i18n.t('orderAggregators:common:order.cityNotes')]}
            />
            <TextField
              name="shipperBuilding"
              label={i18n.t('orderAggregators:common:order.buildings')}
              maxLength={20}
              placeholder={i18n.t(
                'orderAggregators:common:order.buildingsPlaceholder'
              )}
              notes={[i18n.t('orderAggregators:common:order.buildingsNotes')]}
            />
            <TextField
              name="shipperCompany"
              label={i18n.t('orderAggregators:common:order.company')}
              maxLength={20}
              placeholder={i18n.t(
                'orderAggregators:common:order.companyPlaceholder'
              )}
              notes={[i18n.t('orderAggregators:common:order.companyNotes')]}
            />
            <TextField
              type="tel"
              name="shipperTelephone"
              validate={value =>
                composeValidators(required, mustBeNumber)(value)
              }
              label={i18n.t('orderAggregators:common:order.tel')}
              maxLength={12}
              required={true}
              placeholder={i18n.t(
                'orderAggregators:common:order.telPlaceholder'
              )}
              onBlur={value =>
                handleBlurNumbers(form, 'shipperTelephone', value)
              }
              onKeyDown={event => allowExactKeysOnly(event, /[^-]/)}
            />
          </React.Fragment>
        </React.Fragment>
      )}
    </div>
  </div>
);

const DialogFooter = ({ handleSubmit, invalid, close, isConfirm }) => (
  <div className="footer-btn-wrapper">
    {isConfirm ? (
      <Button buttonType="B" type="button" onClick={close} disabled={invalid}>
        {i18n.t('common:ok')}
      </Button>
    ) : (
      <Button
        buttonType="B"
        type="button"
        onClick={handleSubmit}
        disabled={invalid}
      >
        {i18n.t('common:save')}
      </Button>
    )}
  </div>
);

class ShipperDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {};
  }

  render() {
    const {
      className,
      onSubmit,
      dialogRef,
      initialValues,
      fetchAddress,
      referringDataTemplate,
      isInvalidReceiverValues,
      isConfirm,
      orders,
      index,
      options
    } = this.props;

    return (
      <div className={className}>
        <FormDialogrenderer
          title={i18n.t('orderAggregators:common:order.shipperDialogTitle')}
          innerRef={dialogRef}
          renderBody={({ form }) => (
            <DialogBody
              fetchAddress={fetchAddress}
              form={form}
              referringDataTemplate={referringDataTemplate}
              isInvalidReceiverValues={isInvalidReceiverValues}
              isConfirm={isConfirm}
              orders={orders}
              index={index}
              options={options}
            />
          )}
          renderFooter={props => (
            <DialogFooter {...props} isConfirm={isConfirm} />
          )}
          initialValues={initialValues}
          onSubmit={onSubmit}
          showCloseAlert={!isConfirm}
        />
      </div>
    );
  }
}

const commonStyle = css`
  .footer-btn-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .image-wrapper {
    text-align: center;
  }

  .copy-shipper-wrapper {
    display: flex;
    align-items: center;
    line-height: 12px;

    ${SelectField} {
      margin: 0;
    }

    span {
      display: inline-block;
      margin-left: 8px;
    }
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  .form {
    padding: 40px 32px 0;
  }

  .image-wrapper {
    margin-bottom: 40px;
  }

  .input-postcode {
    margin-bottom: 16px;
  }

  .postcode-wrapper {
    margin-bottom: 40px;
  }

  .order-content {
    margin: 40px 0;
  }

  .copy-shipper-wrapper {
    ${SelectField} {
      width: 164px;
    }

    span {
      ${FontStyles.text7};
    }
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  .image-wrapper {
    margin: 24px 0;
  }

  .postcode-wrapper {
    margin-bottom: 32px;

    ${TextField} {
      margin-bottom: 16px;
    }

    button {
      display: flex;
      justify-content: center;
      margin: 0 auto;
      align-items: center;
    }
  }

  .order-content {
    margin: 24px 16px 0;
  }

  .copy-shipper-wrapper {
    ${SelectField} {
      width: 180px;
      padding-right: 0;
    }

    span {
      ${FontStyles.text5};
    }
  }
`;

export default applyStyles(ShipperDialog)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/components/ShipperDialog.js